import { HttpClient } from '@angular/common/http';
import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OktaAuthStateService } from '@okta/okta-angular';
import { catchError, distinctUntilChanged, map, of, startWith, switchMap } from 'rxjs';

import { environment } from '../../../environments/environment';

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  initials: string;
  given_name?: string;
  family_name?: string;
  oktaSub: string;
  createdAt?: string;
}

type CurrentUserResponse = Omit<CurrentUser, 'initials' | 'name'> & {
  name?: string | null;
};

interface UserPresentation {
  name: string;
  initials: string;
}

interface CurrentUserState {
  user: CurrentUser | null;
  claimsPresentation: UserPresentation | null;
}

@Injectable()
export class CurrentUserService {
  private readonly http = inject(HttpClient);
  private readonly authState = inject(OktaAuthStateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly currentUser = signal<CurrentUser | null>(null);
  private readonly claimsPresentation = signal<UserPresentation | null>(null);
  readonly displayName = computed(
    () => this.currentUser()?.name ?? this.claimsPresentation()?.name ?? 'User',
  );
  readonly initials = computed(
    () => this.currentUser()?.initials ?? this.claimsPresentation()?.initials ?? 'U',
  );
  readonly hasPersistedUser = computed(() => this.currentUser() !== null);

  constructor() {
    this.authState.authState$
      .pipe(
        map((state) => ({
          isAuthenticated: state.isAuthenticated,
          claims: state.idToken?.claims,
        })),
        distinctUntilChanged(
          (previous, current) =>
            previous.isAuthenticated === current.isAuthenticated &&
            previous.claims?.sub === current.claims?.sub,
        ),
        switchMap(({ isAuthenticated, claims }) => {
          if (!environment.authenticationRequired) {
            const claimsPresentation = this.presentationFromClaims(claims);
            return this.http.get<CurrentUserResponse>(`${environment.apiUrl}/auth/me`).pipe(
              map((user): CurrentUserState => ({
                user: this.withPresentationFields(this.mergeMissingClaims(user, claims)),
                claimsPresentation: null,
              })),
              catchError(() =>
                of<CurrentUserState>({
                  user: null,
                  claimsPresentation,
                }),
              ),
              startWith({ user: null, claimsPresentation } satisfies CurrentUserState),
            );
          }
          if (!isAuthenticated) {
            console.log('User not authenticated');
            return of<CurrentUserState>({ user: null, claimsPresentation: null });
          }

          const claimsPresentation = this.presentationFromClaims(claims);
          console.log('Fetching user from /auth/me...');
          return this.http.get<CurrentUserResponse>(`${environment.apiUrl}/auth/me`).pipe(
            map((user): CurrentUserState => {
              console.log('Got user from /auth/me:', user);
              const currentUser = this.withPresentationFields(this.mergeMissingClaims(user, claims));
              console.log('Processed currentUser with id:', currentUser.id);
              return {
                user: currentUser,
                claimsPresentation: null,
              };
            }),
            catchError((error) => {
              console.error('Failed to fetch /auth/me:', error);
              return of<CurrentUserState>({
                user: null,
                claimsPresentation: this.presentationFromClaims(claims),
              });
            }),
            startWith({ user: null, claimsPresentation } satisfies CurrentUserState),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ user, claimsPresentation }) => {
        this.currentUser.set(user);
        this.claimsPresentation.set(claimsPresentation);
      });
  }

  private presentationFromClaims(
    claims: Record<string, unknown> | undefined,
  ): UserPresentation | null {
    const subject = claims?.['sub'];
    if (typeof subject !== 'string' || !subject) {
      return null;
    }

    const name = this.resolveName({
      email: this.stringClaim(claims, 'email'),
      name: this.stringClaim(claims, 'name'),
      given_name: this.stringClaim(claims, 'given_name'),
      family_name: this.stringClaim(claims, 'family_name'),
    });
    return { name, initials: this.initialsFor(name) };
  }

  private mergeMissingClaims(
    user: CurrentUserResponse,
    claims: Record<string, unknown> | undefined,
  ): CurrentUserResponse {
    if (!claims) {
      return user;
    }
    return {
      ...user,
      email: user.email || this.stringClaim(claims, 'email'),
      name: user.name || this.stringClaim(claims, 'name'),
      given_name: user.given_name || this.stringClaim(claims, 'given_name') || undefined,
      family_name: user.family_name || this.stringClaim(claims, 'family_name') || undefined,
    };
  }

  private stringClaim(claims: Record<string, unknown>, key: string): string {
    const value = claims[key];
    return typeof value === 'string' ? value : '';
  }

  private withPresentationFields(user: CurrentUserResponse): CurrentUser {
    const name = this.resolveName(user);

    return {
      ...user,
      name,
      initials: this.initialsFor(name),
    };
  }

  private resolveName(
    user: Pick<CurrentUserResponse, 'email' | 'name' | 'given_name' | 'family_name'>,
  ): string {
    return (
      user.name ||
      `${user.given_name ?? ''} ${user.family_name ?? ''}`.trim() ||
      user.email ||
      'User'
    );
  }

  private initialsFor(name: string): string {
    return name
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();
  }
}
